import { makeStyles, tokens } from "@fluentui/react-components";
import { createDarkTheme, createLightTheme } from "@fluentui/react-components";
import type { BrandVariants, Theme } from "@fluentui/react-components";

/**
 * CARBOwaste BDO - Centralized Application Styles
 * 
 * Ten plik zawiera wszystkie style aplikacji w jednym miejscu.
 * Używa Fluent UI 2 Web Light jako podstawy z kolorem brandowym #688BBB.
 * 
 * Struktura:
 * - Temat i kolory brandowe (CARBOwasteTheme)
 * - Wspólne style (root, tooltip, description, table, form)
 * - Style nagłówka (Header component)
 * - Style stopki (Footer component)
 * - Style głównej aplikacji (App component)
 * - Style strony subskrypcji (SubscriptionPage component)
 * - Style strony postępu (ProgressPage component - w progressPageStyles.tsx)
 * 
 * Wszystkie komponenty powinny używać `useAppStyles()` zamiast definiować własne style.
 */

// Kolor podstawowy aplikacji: #688BBB
export const CARBOwasteTheme: BrandVariants = {
  10: "#040608",
  20: "#0F1419",
  30: "#1A2027",
  40: "#252C34",
  50: "#303842",
  60: "#3B4551",
  70: "#475260",
  80: "#535F70",
  90: "#5F6D80",
  100: "#688BBB", // Główny kolor aplikacji
  110: "#7A9AC5",
  120: "#8DA9CF",
  130: "#A0B8D9",
  140: "#B3C7E3",
  150: "#C6D6ED",
  160: "#D9E5F7",
};

// Fluent 2 Web Light jako podstawa
export const standardtheme: Theme = {
  ...createLightTheme(CARBOwasteTheme),
};

export const lightTheme: Theme = {
  ...createLightTheme(CARBOwasteTheme),
};

export const darkTheme: Theme = {
  ...createDarkTheme(CARBOwasteTheme),
};

darkTheme.colorBrandForeground1 = CARBOwasteTheme[110];
darkTheme.colorBrandForeground2 = CARBOwasteTheme[120];

export const useAppStyles = makeStyles({
  // Common styles
  root: {
    minHeight: "100%",
    backgroundColor: tokens.colorNeutralBackground1,
    paddingBottom: "50px", // Reduced since MessageBar is now above footer
  },
  tooltip: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundInverted,
  },
  tooltipContentActive: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    padding: '4px 8px',
    margin: '-4px -8px',
    borderRadius: '3px',
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightRegular,
  },
  tooltipContentInactive: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    padding: '4px 8px',
    margin: '-4px -8px',
    borderRadius: '3px',
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightRegular,
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase300,
    padding: 0,
    margin: 0,
    textAlign: "left",
    paddingTop: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingVerticalXS,
    paddingRight: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
  },
  toolbar: {
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    marginBottom: tokens.spacingVerticalXXS,
  },
  toolbarButton: {
    minWidth: "32px",
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXXS}`,
    fontSize: tokens.fontSizeBase200,
    "& > span": {
      fontSize: tokens.fontSizeBase200,
    },
  },
  backIcon: {
    color: tokens.colorNeutralForeground1,
    fontSize: "12px",
  },
  table: {
    width: "100%",
    padding: 0,
    margin: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    paddingTop: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingVerticalXS,
    paddingRight: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalL,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    maxWidth: "400px",
    margin: "0 auto",
    background: "#fff",
    borderRadius: "8px",
    padding: tokens.spacingVerticalL,
    boxShadow: "0 2px 8px #0001",
    position: "relative",
  },
  // Header
  headerBackground: {
    position: "sticky",
    top: 0,
    zIndex: 1000,
    backgroundColor: tokens.colorNeutralBackground3,
    boxShadow: "0 2px 8px #0001, 0 2px 8px #0001",
  },
  headerContentSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingBottom: "10px",
    paddingTop: "10px",
  },
  headerMenuContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    position: "relative",
  },
  headerMenuButtonLeft: {
    position: "absolute",
    left: 0,
    minWidth: "auto",
  },
  headerMenuButton: {
    minWidth: "auto",
  },
  headerMessageBar: {
    margin: 0,
    borderRadius: 0,
  },
  welcome__header: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingBottom: "10px",
    paddingTop: "10px",
    backgroundColor: tokens.colorNeutralBackground3,
    position: "sticky",
    top: 0,
    zIndex: 1000,
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)",
  },
  statusBarContainer: {
    position: "absolute",
    top: tokens.spacingVerticalXS,
    right: tokens.spacingHorizontalS,
  },
  wrapper: {
    padding: tokens.spacingVerticalS,
    columnGap: "8px",
    display: "flex",
  },

  // Footer
  footer: {
    position: "fixed",
    bottom: 0,
    left: 0,
    width: "99%",
    zIndex: 100,
    padding: tokens.spacingVerticalXXS,
    background: tokens.colorNeutralBackground3,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: tokens.fontSizeBase300,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: "auto",
    boxShadow: "0 -2px 8px #0001",
    gap: tokens.spacingHorizontalS,
    overflow: "hidden",
  },
  footer_logo: {
    // Logo ma już stałe wymiary w SVG: width="48" height="20"
  },
  footerRightSection: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },

  // App.tsx
  app_container: {
    display: "flex",
    padding: tokens.spacingVerticalXS,
    backgroundColor: tokens.colorNeutralBackground1,
    flexDirection: "column",
  },
  app_message: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightRegular,
    padding: 0,
    margin: 0,
    textAlign: "left",
    paddingLeft: tokens.spacingHorizontalS,
  },
  accordionheader: {
    display: "flex",
    flexDirection: "column",
    alignItems: "baseline",
    justifyContent: "left",
  },

  spacing: {
    marginBottom: tokens.spacingVerticalXXS,
    width: "100%",
    height: "1px",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  container: {
    display: "flex",
    padding: 0,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingVerticalXS,
    paddingRight: tokens.spacingVerticalXS,
    backgroundColor: tokens.colorNeutralBackground1,
    flexDirection: "column",
    minHeight: "calc(100vh - 40vh)",
  },
  headerTitle: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForegroundStaticInverted,
    backgroundColor: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightRegular,
    padding: tokens.spacingVerticalXS + " " + tokens.spacingHorizontalS,
    margin: 0,
    marginBottom: tokens.spacingVerticalM,
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    justifyContent: "left",
    gap: "8px",
  },
  buttonBottom: {
    marginTop: "auto",
    alignSelf: "flex-end",
  },
  option: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightRegular,
    padding: tokens.spacingVerticalXS + " " + tokens.spacingHorizontalS,
    margin: 0,
    textAlign: "left",
  },
  action: {
    marginTop: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  //ProgressPage
  progressContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    margin: tokens.spacingVerticalXXS,
    backgroundColor: tokens.colorNeutralBackground1,
    padding: tokens.spacingVerticalM + " " + tokens.spacingHorizontalM,

    minHeight: "300px",
    justifyContent: "top",
  },
  iconLoading: {
    marginBottom: tokens.spacingVerticalM,
    color: "#688BBB",
    fontSize: tokens.fontSizeHero1000,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightHero1000,
  },
  iconSuccess: {
    marginBottom: tokens.spacingVerticalM,
    color: "green",
    fontSize: tokens.fontSizeHero1000,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightHero1000,
  },
  iconError: {
    marginBottom: tokens.spacingVerticalM,
    color: "red",
    fontSize: tokens.fontSizeHero1000,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightHero1000,
  },
  progressLoadingMessage: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: "#688BBB",
    marginTop: tokens.spacingHorizontalXS,
  },
  progressSuccessMessage: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: "green",
    marginTop: tokens.spacingHorizontalXS,
  },
  progressErrorMessage: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: "red",
    marginTop: tokens.spacingHorizontalXS,
  },
  // CompoundButton styles
  compoundButtonFullWidth: {
    width: "100%",
    justifyContent: "flex-start",
  },
  compoundButtonSubtleHover: {
    width: "100%",
    justifyContent: "flex-start",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":hover:active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },

  // Header styles
  headerContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: tokens.spacingVerticalM + " " + tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    minHeight: "60px",
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
  },
  headerLogo: {
    flexShrink: 0,
  },
  headerTextContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
  },
  headerTitleText: {
    color: tokens.colorNeutralForeground1,
  },
  headerSubtitleText: {
    color: tokens.colorNeutralForeground2,
  },
  headerStatus: {
    color: tokens.colorNeutralForeground3,
  },

  // Page styles (used by all pages accessed from menu)
  appPageContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  appPageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXXS,
    paddingLeft: tokens.spacingHorizontalXXS,
    paddingRight: tokens.spacingHorizontalXXS,
    backgroundColor: "#688BBB",
    gap: tokens.spacingHorizontalXS,
  },
  appPageHeaderIcon: {
    color: tokens.colorNeutralForegroundInverted,
    fontSize: "18px",
    flexShrink: 0,
  },
  appPageBackButton: {
    minWidth: "auto",
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
  },
  appPageTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForegroundInverted,
    lineHeight: tokens.lineHeightBase400,
  },
  // SubscriptionPage specific styles
  subscriptionContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  subscriptionContent: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
  },
  subscriptionCard: {
    padding: tokens.spacingVerticalM,
  },
  subscriptionInfoRow: {
    display: "flex",
    flexDirection: "column",
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXXS,
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  subscriptionLabel: {
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    textAlign: "left",
    marginBottom: "2px",
  },
  subscriptionValue: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    textAlign: "right",
  },
  subscriptionSectionHeader: {
    backgroundColor: tokens.colorNeutralBackground2,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusSmall,
    marginBottom: tokens.spacingVerticalS,
  },
  subscriptionSectionTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
  },
  subscriptionStatusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXXS,
    borderRadius: tokens.borderRadiusMedium,
  },
  subscriptionStatusActive: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
  },
  subscriptionStatusTrial: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2,
  },
  subscriptionStatusInactive: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
  },
  // Report code badge (CWxx)
  reportCodeBadge: {
    display: "inline-block",
    verticalAlign: "baseline",
    fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    backgroundColor: "#D9E5F7",
    color: "#252C34",
    paddingLeft: tokens.spacingHorizontalXS,
    paddingRight: tokens.spacingHorizontalXS,
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXXS,
    borderRadius: tokens.borderRadiusSmall,
    marginRight: tokens.spacingHorizontalXS,
    letterSpacing: "0.5px",
  },
  // Report title text style
  reportTitleText: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  subscriptionActionButtonsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    width: "100%",
  },
  subscriptionActionButton: {
    width: "100%",
  },
  subscriptionActivateButton: {
    width: "100%",
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundInverted,
    ":hover": {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
    ":active": {
      backgroundColor: tokens.colorBrandBackgroundPressed,
    },
  },
  subscriptionUnregisterButton: {
    width: "100%",
    backgroundColor: tokens.colorPaletteRedBackground3,
    color: tokens.colorNeutralForegroundInverted,
    ":hover": {
      backgroundColor: tokens.colorPaletteRedBackground2,
    },
    ":active": {
      backgroundColor: tokens.colorPaletteRedBackground1,
    },
  },
  subscriptionCloseButtonContainer: {
    marginTop: tokens.spacingVerticalXXL,
    display: "flex",
    justifyContent: "center",
  },
  subscriptionCloseButton: {
    minWidth: "200px",
  },
  // Help/Info page styles
  appPageContent: {
    padding: tokens.spacingHorizontalL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  appSectionTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalS,
  },
  appLinkParagraph: {
    marginTop: tokens.spacingVerticalXXS,
    marginBottom: tokens.spacingVerticalXXS,
  },
  appLinkButton: {
    padding: 0,
    minWidth: 'auto',
    textDecoration: 'none',
    color: tokens.colorBrandForegroundLink,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    minHeight: "auto",
    lineHeight: tokens.lineHeightBase300,
    ":hover": {
      textDecoration: 'underline',
    },
  },
  appExternalLinkIcon: {
    marginLeft: tokens.spacingHorizontalXXS,
    fontSize: "6px",
  },
  appLinkButtonLarge: {
    padding: tokens.spacingVerticalS,
    minWidth: 'auto',
    textDecoration: 'none',
    color: tokens.colorBrandForegroundLink,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    minHeight: "auto",
    lineHeight: tokens.lineHeightBase400,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    ":hover": {
      textDecoration: 'underline',
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
  },
  appLinksSection: {
    marginTop: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalS,
    textAlign: "center",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  appLinkText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    lineHeight: tokens.lineHeightBase200,
  },
  appExternalLink: {
    color: tokens.colorBrandForeground1,
    textDecoration: "none",
    fontSize: tokens.fontSizeBase200,
    "&:hover": {
      textDecoration: "underline",
    },
  },

  // Network Error Dialog
  errorDialogSurface: {
    borderTopWidth: "4px",
    borderTopStyle: "solid",
    borderTopColor: tokens.colorPaletteRedBorder1,
  },
  errorDialogTitle: {
    color: tokens.colorPaletteRedForeground1,
  },

  // Retry Connection Overlay - subtelny i elegancki
  retryOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 255, 255, 0.85)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  retryContainer: {
    backgroundColor: tokens.colorNeutralBackground1,
    padding: "24px 32px",
    borderRadius: "12px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  retryIcon: {
    fontSize: "40px",
    color: "#107c10", // Wyrazisty zielony kolor
    animationName: {
      "0%, 100%": {
        opacity: 1,
        transform: "scale(1)",
        filter: "drop-shadow(0 0 0px rgba(16, 124, 16, 0))",
      },
      "50%": {
        opacity: 0.7,
        transform: "scale(1.15)",
        filter: "drop-shadow(0 0 8px rgba(16, 124, 16, 0.6))",
      },
    },
    animationDuration: "1.2s",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
  },

  // Activation Dialog styles
  activationDialogOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
  },
  activationDialogContainer: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
    width: "90%",
    maxWidth: "500px",
    display: "flex",
    flexDirection: "column",
    maxHeight: "90vh",
  },
  activationDialogHeader: {
    padding: tokens.spacingVerticalL + " " + tokens.spacingHorizontalXL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  activationDialogTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalXXS,
  },
  activationDialogDescription: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  activationDialogContent: {
    padding: tokens.spacingVerticalL + " " + tokens.spacingHorizontalXL,
    flex: 1,
    overflowY: "auto",
  },
  activationDialogTextarea: {
    width: "100%",
    minHeight: "120px",
    fontFamily: "'Courier New', monospace",
    fontSize: tokens.fontSizeBase300,
    resize: "vertical",
  },
  activationDialogCharCounter: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXXS,
  },
  activationDialogFooter: {
    padding: tokens.spacingVerticalL + " " + tokens.spacingHorizontalXL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    display: "flex",
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalS,
  },
  activationDialogError: {
    marginBottom: tokens.spacingVerticalM,
  },
});
